<script setup>
import { computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import { useStore } from 'dashboard/composables/store';
import Icon from '../icon/Icon.vue';

const props = defineProps({
  hasAssistants: {
    type: Boolean,
    default: false,
  },
  activeAssistant: {
    type: Object,
    default: () => ({}),
  },
});

const emit = defineEmits(['useSuggestion']);
const { t } = useI18n();
const route = useRoute();
const store = useStore();

const getCurrentContext = () => {
  const path = route.path;
  if (path.includes('/conversations')) return 'conversation';
  if (path.includes('/dashboard')) return 'dashboard';
  return 'dashboard';
};

// Fetch prompts when assistant changes
watch(
  () => props.activeAssistant?.id,
  newId => {
    if (newId) {
      store.dispatch('copilotPrompts/fetch', { assistantId: newId });
    }
  },
  { immediate: true }
);

const promptOptions = computed(() => {
  const context = getCurrentContext();
  const allPrompts = store.getters['copilotPrompts/getRecords'];

  return allPrompts
    .filter(p => p.context_type === context && !p.deleted_at)
    .map(p => ({
      label: p.label,
      prompt: p.content,
    }));
});

const handleSuggestion = opt => {
  emit('useSuggestion', opt.prompt);
};
</script>

<template>
  <div class="flex-1 flex flex-col gap-6 px-2">
    <div class="flex flex-col space-y-4 py-4">
      <Icon icon="i-woot-captain" class="text-n-slate-9 text-4xl" />
      <div class="space-y-1">
        <h3 class="text-base font-medium text-n-slate-12 leading-8">
          {{ $t('CAPTAIN.COPILOT.PANEL_TITLE') }}
        </h3>
        <p class="text-sm text-n-slate-11 leading-6">
          {{ $t('CAPTAIN.COPILOT.KICK_OFF_MESSAGE') }}
        </p>
      </div>
    </div>
    <div v-if="!hasAssistants" class="w-full space-y-2">
      <p class="text-sm text-n-slate-11 leading-6">
        {{ $t('CAPTAIN.ASSISTANTS.NO_ASSISTANTS_AVAILABLE') }}
      </p>
      <router-link
        :to="{
          name: 'captain_assistants_create_index',
          params: {
            accountId: route.params.accountId,
          },
        }"
        class="text-n-slate-11 underline hover:text-n-slate-12"
      >
        {{ $t('CAPTAIN.ASSISTANTS.ADD_NEW') }}
      </router-link>
    </div>
    <div v-else-if="promptOptions.length > 0" class="w-full space-y-2">
      <span class="text-xs text-n-slate-10 block">
        {{ $t('CAPTAIN.COPILOT.TRY_THESE_PROMPTS') }}
      </span>
      <div class="space-y-1">
        <button
          v-for="prompt in promptOptions"
          :key="prompt.label"
          class="w-full px-3 py-2 rounded-md border border-n-weak bg-n-slate-2 text-n-slate-11 flex items-center justify-between hover:bg-n-slate-3 transition-colors"
          @click="handleSuggestion(prompt)"
        >
          <span>{{ prompt.label }}</span>
          <Icon icon="i-lucide-chevron-right" />
        </button>
      </div>
    </div>
  </div>
</template>
